// The row of headline numbers at the top of a test: players, exposures, days
// running, split health and the primary metric's change.
import React from 'react';
import { Card, Col, Progress, Row, Space, Statistic, Tag, Typography } from 'antd';
import type { ExpV2Counts, ExpV2Detail, ExpV2Results } from '@/types/experimentsV2';
import { istTime, srmBadge } from './display';
import {
  daysSince,
  describeChange,
  formatCount,
  isCatalogueMetric,
  metricLabel,
  startedAt,
  variantColor,
  variantOrder,
} from './results';
import { VerdictTag } from './Scorecard';

interface Props {
  test: ExpV2Detail;
  counts: ExpV2Counts | null;
  results: ExpV2Results | null;
  now: Date;
}

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 4,
      background: color,
      marginRight: 4,
    }}
  />
);

export const SummaryStrip: React.FC<Props> = ({ test, counts, results, now }) => {
  const order = variantOrder(test);
  const assigned = counts?.variants.reduce((n, v) => n + v.assigned, 0) ?? 0;
  const open = counts?.variants.reduce((n, v) => n + v.open, 0) ?? 0;
  const exposed = counts?.variants.reduce((n, v) => n + v.exposed, 0) ?? 0;
  const start = startedAt(test);
  const days = daysSince(start, now);
  const planned = test.preRegistration?.plannedEndDate;
  const plannedDays =
    start && planned
      ? Math.max(
          1,
          (new Date(`${planned}T23:59:59+05:30`).getTime() - new Date(start).getTime()) / 86_400_000
        )
      : null;
  const srm = counts ? srmBadge(counts) : null;
  const primary = results?.metrics.find((m) => m.role === 'primary');
  const primaryText = test.preRegistration?.primaryMetric;

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small" style={{ height: '100%' }}>
          <Statistic title="Players in the test" value={counts ? formatCount(assigned) : '—'} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {counts
              ? counts.variants.map((v) => (
                  <span key={v.variant} style={{ marginRight: 8 }}>
                    <Dot color={variantColor(order, v.variant)} />
                    {v.variant} {formatCount(v.assigned)}
                  </span>
                ))
              : 'No counts yet'}
          </Typography.Text>
          {counts && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {formatCount(open)} in it now · since{' '}
                {counts.stablePhaseFrom
                  ? `${istTime(counts.stablePhaseFrom)} IST (full range)`
                  : 'the start'}
              </Typography.Text>
            </div>
          )}
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={5}>
        <Card size="small" style={{ height: '100%' }}>
          <Statistic
            title="Saw the tested surface"
            value={counts ? formatCount(exposed) : '—'}
            suffix={
              counts && assigned > 0 ? (
                <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                  ({Math.round((exposed / assigned) * 1000) / 10}%)
                </Typography.Text>
              ) : undefined
            }
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Players with an exposure logged, any arm
          </Typography.Text>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={5}>
        <Card size="small" style={{ height: '100%' }}>
          <Statistic
            title="Running"
            value={days === null ? '—' : days.toFixed(1)}
            suffix={days === null ? undefined : 'days'}
          />
          {plannedDays && days !== null ? (
            <Progress
              percent={Math.min(100, Math.round((days / plannedDays) * 100))}
              size="small"
              format={() => `to ${planned}`}
            />
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              No planned end date
            </Typography.Text>
          )}
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Card size="small" style={{ height: '100%' }}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Typography.Text type="secondary">Primary metric</Typography.Text>
            {primary ? (
              <>
                <Typography.Text strong>{primary.label}</Typography.Text>
                {primary.comparisons.map((c) => (
                  <Space key={c.variant} wrap>
                    <Typography.Text>{c.variant}:</Typography.Text>
                    <Typography.Text strong style={{ fontSize: 18 }}>
                      {c.verdict === 'not_enough_data' ? '—' : describeChange(primary, c).main}
                    </Typography.Text>
                    <VerdictTag verdict={c.verdict} />
                  </Space>
                ))}
              </>
            ) : (
              <Typography.Text>
                {primaryText
                  ? isCatalogueMetric(primaryText)
                    ? `${metricLabel(primaryText)}: computing…`
                    : `"${primaryText}" (not a standard metric)`
                  : 'Not set'}
              </Typography.Text>
            )}
            {srm && (
              <span>
                <Tag color={srm.color}>{srm.text}</Tag>
              </span>
            )}
          </Space>
        </Card>
      </Col>
    </Row>
  );
};
